import React, { useMemo } from 'react';
import { Text } from './ui/AppText';

/**
 * Componente que renderiza texto com suporte a negrito
 * Uso: **texto** será renderizado em negrito
 *
 * Exemplo: "Este é um **texto importante** aqui"
 * 
 * OTIMIZADO: usa useMemo para evitar reprocessamento do regex a cada render
 */
const FormattedText = ({ children, style, ...props }) => {
  // Memoiza o parsing do texto para evitar regex em cada render
  const parts = useMemo(() => {
    if (!children || typeof children !== 'string') {
      return null;
    }

    if (!children.includes('**')) {
      return null;
    }

    // Regex para encontrar **texto**
    const regex = /\*\*(.*?)\*\*/g;
    const result = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(children)) !== null) {
      // Adiciona texto antes do negrito
      if (match.index > lastIndex) {
        result.push({
          text: children.substring(lastIndex, match.index),
          bold: false,
        });
      }

      // Adiciona texto em negrito
      result.push({
        text: match[1],
        bold: true,
      });

      lastIndex = regex.lastIndex;
    }

    // Adiciona texto restante
    if (lastIndex < children.length) {
      result.push({
        text: children.substring(lastIndex),
        bold: false,
      });
    }

    return result.length > 0 ? result : null;
  }, [children]);

  // Se não encontrou nenhum negrito ou não é string, renderiza texto normal
  if (!parts) {
    return <Text style={style} {...props}>{children}</Text>;
  }

  return (
    <Text style={style} {...props}>
      {parts.map((part, index) => (
        <Text key={index} style={part.bold ? { fontWeight: 'bold' } : undefined}>
          {part.text}
        </Text>
      ))}
    </Text>
  );
};

// Memoiza o componente inteiro para evitar re-renders desnecessários
export default React.memo(FormattedText);
